'use client';

import { ButtonStyleType } from '@udemy-v2/react-core-components';
import { ConsumerSubscriptionPlan, SubscriptionPlanProductType, useConsumerSubscriptionGwGPlan, useConsumerSubscriptionGwGPlusPersonalPlan } from '@udemy-v2/subscription-graphql';
import { useFeatures } from '@udemy-v2/react-experimentation';
import { useI18n } from '@udemy-v2/i18n';
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useClpCourse } from '@/features/course-landing-page/context/clp-course-provider.react-component';
import { MODES } from '../components/redeem-coupon/constants';
import { IconName } from '@udemy-v2/icons';
import { SidebarTabType } from '../components/course-sidebar/course-sidebar-tabs-container.react-component';
import { CtaBehaviour, useClpCtaBehaviour } from '@/features/course-landing-page/context/hooks/use-clp-cta-behaviour';
import { useCoursePrice, useCoursePriceStore } from '@udemy-v2/react-course-pricing';
import { DiscountAttempt, useShoppingListStore } from '@udemy-v2/api-shopping';
import { transformCoursePriceToDiscount } from '@/lib/transforms/course-price-to-discount';
import { toJS } from 'mobx';
import { useMatchMedia } from '@udemy-v2/design-system-utils';
import { ClpPurchaseBoxSubscriptionPlan, useClpSubscriptionContext } from '@/features/course-landing-page/context/clp-subscription-context.react-component';
import { publishCouponApplyEvent } from '@/features/course-landing-page/events/coupon-events';
import { useNonCatalogPPPricing } from '@/features/course-landing-page/hooks/use-non-catalog-pp-pricing';
import { useUserHasOrganization } from '@/lib/hooks/use-user-has-organization';
import { SUBSCRIPTION_PLAN_PRICING_AVAILABILITY } from '@/features/course-landing-page/constants';
import { ReactCLPSelectedOptionContext } from './hooks/use-clp-selected-option';
import { CommonDataSubscriptionStatusEnum } from '@udemy-v2/common-data';
export type BuyButtonStyle = Extract<ButtonStyleType, 'primary' | 'secondary' | 'brand'>;

// Narrow type to ensure we only use the cart icon and that it correctly references Design System icon
type AddToCartIcon = Extract<IconName, 'cart'>;

/** There are currently three states for the purchase options on the CLP:
 * 1. Subscription
 * 2. Transactional - within the transactional option, we might display: add to cart or add to cart and buy now.
 *    The ordering and presentation of these sub-states is determined by the TransactionalCTAContext
 * 3. Loading - we have not yet fetched all the data needed to determine which purchase option to render
 **/
export const enum CLPPurchaseOption {
  SUBSCRIPTION = 'subscription',
  TRANSACTIONAL = 'transactional',
  LOADING = 'loading',
}

/** Legacy: This will likely be cleaned up and change as the purchase box is built out.
 * Organizes the component context from the backend with derived fields (ie style, enabled state)
 * that depend on the orchestrated state of the CLP purchase context (accounting for subs, discounts, promos, etc)
 * This allows us to reduce our dependency on the monolith context while making it easier to reason about the
 * state of each of the purchase options on the page. **/
export interface TransactionalCTAContext {
  /* Flag indicating whether or not to display a given CTA button */
  isEnabled: boolean;
  /* Flag indicating whether or not the button is the primary CTA on the page. This determines display order and which
  CTA option should be included in the slider menu on mobile. */
  isPrimary: boolean;
  buttonText?: string;

  /* The button styling to apply to a given CTA button. For most CLPs, the selected purchase option CTA button should use
   * 'primary', and if the selected purchase option is transactional for a non-PP course (ie display both add to cart
   *  and buy now options) then the isPrimary flag will determine whether a given button is styled with 'primary' or 'secondary'. */
  udStyle: BuyButtonStyle;
  iconName?: AddToCartIcon;
}
export interface SubscriptionCTAConfiguration {
  directCheckout: boolean;
  selectPlanText?: string;
  purchaseCtaText?: string;
  /** Upgrade URL for VP→PP tier upgrade flow. When set, overrides the default checkout URL. */
  upgradeUrl?: string;
}
export interface CLPPurchaseContext {
  isConsumerSubscriptionPaused: boolean;
  /** Indicates the initial loading state for the entire sidebar (first render) */
  isLoading: boolean;
  /** Indicates price is being reloaded (e.g., after coupon application/removal) */
  isReloadingPrice: boolean;
  isFree: boolean;
  isFreeWithDiscount: boolean;
  isPublishedAndNonPrivate: boolean;
  isEnrolledInCourse: boolean;
  unwrappedConsumerSubscriptionPlan?: ClpPurchaseBoxSubscriptionPlan;
  isCourseInAvailablePlans: boolean;
  isCourseIncludedInUserSubscription: boolean;
  isCourseIncludedInGrowWithGoogleSubscription: boolean;
  isPersonalPlanSubscriber: boolean;
  isTransactionalDiscountEnabled?: boolean;
  isPersonalPlanDiscountEnabled?: boolean;
  showLifetimeAccess?: boolean;
  showLifetimeAccessInBuyBox?: boolean;
  subscriptionCTAConfiguration?: SubscriptionCTAConfiguration;
  nonCatalogSubscriptionCTAConfiguration?: SubscriptionCTAConfiguration;
  /* Tab state management */
  renderTabContainer: boolean;
  activeSidebarTab: SidebarTabType;
  setActiveSidebarTab: (tab: SidebarTabType) => void;
  /* Coupon management */
  couponStore: import('@udemy-v2/api-shopping').CouponStore;
  handleCouponApply: () => Promise<void>;
  handleCouponRemove: (code: string) => Promise<void>;
  /* Discount attempts for display (cart: from cart API, non-cart: from pricing API) */
  currentDiscountAttempts: DiscountAttempt[];
  /* The error message to display in the coupon form. Derived from both cart and pricing API. */
  couponErrorMessage?: string;
  showUdemyBusinessContent?: boolean;
  useCollapsiblePurchaseSection?: boolean;
  isEligibleForNonCatalogPPOffer: boolean;
  isFeaturesLoading: boolean;
  subscriptionTiersEnabled: boolean;
  clpGenericUdemyPlansCopyEnabled: boolean;
  starterPlanUpsellCLPCopyUpdatesEnabled: boolean;
  showUpgradeCTA: boolean;
  /** True when the user/course meet all gating criteria and a variant is assigned. */
  isEligibleForBlendedOffer: boolean;
  /** Raw blended_monetization_promo feature assignment, exposed for ExperimentImpressionEvent. */
  blendedOfferFeature?: ReturnType<ReturnType<typeof useFeatures>['featureVariantAssignments']['get']>;
  nonCatalogPersonalPlan?: ConsumerSubscriptionPlan;
  /** GR-87: marketplace-only price box experiment — hides subscription option */
  removeSubscriptionOptionEnabled: boolean;
  /* Grow with Google subscription plan data */
  gwgPlan?: ReturnType<typeof useConsumerSubscriptionGwGPlan>;
  gwgPlusPersonalPlan?: ReturnType<typeof useConsumerSubscriptionGwGPlusPersonalPlan>;
  isMonthlyPersonalPlanAvailable?: boolean;
}

// Do not use externally: exported for testing purposes only.
export const _resolveCouponErrorMessage = ({
  currentMode,
  submittedCode,
  couponStoreError,
  currentDiscountAttempts,
  isReloadingPrice
}: {
  currentMode: string;
  submittedCode?: string;
  couponStoreError?: string;
  currentDiscountAttempts: DiscountAttempt[];
  isReloadingPrice: boolean;
}) => {
  // Never show a message if the coupon form is in a loading state.
  if (currentMode === MODES.PENDING || isReloadingPrice) {
    return '';
  }

  // If the coupon store has a submitted code stored:
  // (i.e, if the user has manually input a code)
  if ((submittedCode ?? '').length > 0) {
    // Then check if the pricing API indicates that that code has been successfully applied.
    if (currentDiscountAttempts.some(discountAttempt => discountAttempt.code === submittedCode)) {
      // If so, show the user no error as they are receiving the discount. The cart API is lying to us in this case about
      // the code not being successfully applied, so we go with what the pricing api says.
      return '';
    }
    // If the pricing API indicates that there is no discount that matches the submitted code, then the code has indeed failed
    // to be applied, and we want to show the user the associated error.
    else {
      return couponStoreError;
    }
  } else {
    // If there is no submitted code stored, then show the user no error, as any applied coupons or errors are not from
    // manual input.
    return '';
  }
};
export const ReactCLPPurchaseContext = createContext<CLPPurchaseContext | undefined>(undefined);
export type { CLPSelectedOptionContext } from './hooks/use-clp-selected-option';
export { ReactCLPSelectedOptionContext, useClpSelectedOption } from './hooks/use-clp-selected-option';
export interface CLPPurchaseContextProviderProps {
  children: ReactNode;
}

/**
 * This provider is the central store for all CLP business logic around purchase options / price box / enrollment options.
 * It orchestrates data coming from the monolith CLP context, the subscription plan context,
 *  - clc store / monolith backend component contexts (to determine if course is in PP and if it's discounted)
 *  - asyncFeatureContext (for dynamic CTA feature flag and any other experimentation logic affecting purchase options)
 *  - SubscriptionPlanContext (for Personal Plan pricing and availability and PP subscriber status)
 *  - ReactCLPNoticeContext (for subs discount use-case, uses data fetched from CMP)
 *  - CLP context (to determine if user is UB user)
 * Provider that fetches subscription plan information from GraphQL and provides it alongside the subscription context
 * coming from the CLC context. This provider also orchestrates the interpolation of the CLP price box notice data with
 * the subscription plan data.
 * @param props
 * @constructor
 */
export const CLPPurchaseContextProvider: React.FC<CLPPurchaseContextProviderProps> = props => {
  const {
    gettext
  } = useI18n();
  const hasOrganization = useUserHasOrganization();
  const course = useClpCourse();
  const coursePrice = useCoursePrice(Number(course.id));
  const isMobile = useMatchMedia('sm-max');
  const {
    ctaBehaviour,
    isLoading: isCtaBehaviourLoading
  } = useClpCtaBehaviour({
    courseId: course.id
  });
  const {
    unwrappedConsumerSubscriptionPlan,
    isPersonalPlanSubscriber,
    isCourseIncludedInUserSubscription,
    isCourseInAvailablePlans,
    isCourseInPersonalPlanCatalog,
    isPersonalPlanLoading,
    isSubscriptionPlanLoading,
    isCourseInValuePlanCatalog,
    isValuePlanSubscriber,
    subscribedProductType,
    subscribedConsumerSubscriptionPlan
  } = useClpSubscriptionContext();
  const isConsumerSubscriptionPaused = subscribedConsumerSubscriptionPlan?.status === CommonDataSubscriptionStatusEnum.Paused;

  // Fetch Grow with Google subscription plans
  const gwgPlan = useConsumerSubscriptionGwGPlan();
  const gwgPlusPersonalPlan = useConsumerSubscriptionGwGPlusPersonalPlan();
  const {
    isLoading: isFeaturesLoading,
    featureVariantAssignments: features
  } = useFeatures(['new_price_box_on_clp', 'non_catalog_clps_promo', 'value_planstarter_plan', 'remove_subscription_option', 'blended_monetization_promo', 'starter_plan_upsell', SUBSCRIPTION_PLAN_PRICING_AVAILABILITY]);
  const featureFlag = features.get('new_price_box_on_clp');
  const collapsiblePurchaseBoxFeatureEnabled = featureFlag?.configuration?.showNewPriceBox ?? false;
  const removeSubsOptionFeature = features.get('remove_subscription_option');
  const removeSubsOptionValue = (removeSubsOptionFeature?.configuration?.removeSubscriptionOption ?? false) as boolean;
  const subscriptionTiersEnabled = (features.get('value_planstarter_plan')?.configuration?.enableValuePlan ?? false) as boolean;
  const clpGenericUdemyPlansCopyEnabled = (features.get('value_planstarter_plan')?.configuration?.clpGenericUdemyPlansCopy ?? false) as boolean;
  const starterPlanUpsellCLPCopyUpdatesEnabled = (features.get('starter_plan_upsell')?.configuration?.enableCLPCopyUpdates ?? false) as boolean;
  const subscriptionPlanPricingAvailabilityConfig = features.get(SUBSCRIPTION_PLAN_PRICING_AVAILABILITY)?.configuration as {
    is_monthly_personal_plan_available?: boolean;
  } | undefined;
  const isMonthlyPersonalPlanAvailable = subscriptionPlanPricingAvailabilityConfig?.is_monthly_personal_plan_available ?? true;
  const nonCatalogClpFlag = features.get('non_catalog_clps_promo');
  const nonCatalogPPOfferEnabled = nonCatalogClpFlag?.configuration?.non_catalog_clp_enabled ?? false;
  const nonCatalogCtaOrder = nonCatalogClpFlag?.configuration?.ctaOrder as string | undefined;
  const blendedOfferFeature = features.get('blended_monetization_promo');
  const blendedOfferEnabled = (blendedOfferFeature?.configuration?.blended_clp_enabled ?? false) as boolean;

  // Shopping list store for coupon management
  const shoppingStore = useShoppingListStore();
  const couponStore = shoppingStore.couponStore;

  // Course price store for triggering reloads
  const coursePriceStore = useCoursePriceStore();

  // State for active sidebar tab
  const [activeSidebarTab, setActiveSidebarTab] = useState<SidebarTabType>(SidebarTabType.PERSONAL);

  // Derive pricing discount attempts from coursePrice (non-cart scenarios only)
  // This is derived from the pricing API response and passed to ClpCouponProvider
  const pricingDiscountAttempts = useMemo(() => {
    if (coursePrice?.status !== 'loaded' || !coursePrice.code) {
      return [];
    }
    const plainCoursePrice = toJS(coursePrice);
    const discount = transformCoursePriceToDiscount(plainCoursePrice);
    return discount ? [discount] : [];
  }, [coursePrice]);

  // Track whether we're reloading the price (after initial load)
  const [hasLoadedPriceOnce, setHasLoadedPriceOnce] = useState(false);

  // Track which URL param codes we've already fired events for (to prevent duplicates)
  const firedUrlParamCodesRef = useRef<Set<string>>(new Set());

  // Update hasLoadedPriceOnce during render when price finishes loading for the first time
  if (coursePrice?.status === 'loaded' && !hasLoadedPriceOnce) {
    setHasLoadedPriceOnce(true);
  }

  // Determine if we're reloading the price
  // This is true when:
  // 1. We've loaded the price at least once before
  // 2. The price is currently loading
  const isReloadingPrice = hasLoadedPriceOnce && coursePrice?.status === 'loading';

  /**
   * Buyables array for tracking coupon events
   */
  const buyables = useMemo(() => [{
    type: 'course' as const,
    id: Number(course.id),
    trackingId: course.frontendTrackingId || ''
  }], [course.id, course.frontendTrackingId]);

  /**
   * Unified coupon application handler
   *
   * Applies coupon code and reloads pricing:
   * 1. Adds code to URL (makes discountCodeProvider return it)
   * 2. If course in cart, also updates cart via cart API
   * 3. Reloads price → pricing API validates the code
   * 4. URL sync effect keeps or removes code based on pricing API response
   */
  const handleCouponApply = useCallback(async () => {
    const codeToApply = couponStore.inputCode;
    if (hasOrganization || !codeToApply || !couponStore.isValidInputCode) {
      return;
    }
    try {
      // Always add code to URL for CLP display
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.set('couponCode', codeToApply);
      window.history.replaceState({}, '', `${window.location.pathname}?${urlParams.toString()}`);

      // Mark this code as manually applied so we don't fire URL param event for it
      firedUrlParamCodesRef.current.add(codeToApply);

      // Also apply to cart (cart will handle if course is not in cart)
      await couponStore.applyCouponCode(codeToApply, 'manual_entry', buyables);

      // reload the price regardless of if there's an error or not.
      coursePriceStore.reloadCourse(Number(course.id));
    } catch (error) {
      console.error('[CLPPurchaseContext] Error applying coupon:', error);
    }
  }, [couponStore, coursePriceStore, course.id, buyables, hasOrganization]);

  /**
   * Unified coupon removal handler
   *
   * Removes coupon code and reloads pricing:
   * 1. Removes code from URL (makes discountCodeProvider return undefined)
   * 2. If course in cart, also removes from cart via cart API
   * 3. Reloads price → pricing API returns price without discount
   */
  const handleCouponRemove = useCallback(async (code: string) => {
    try {
      // Always remove URL param immediately
      // This ensures discountCodeProvider returns undefined on next price fetch
      if (typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('couponCode')) {
          urlParams.delete('couponCode');
          const newUrl = urlParams.toString() ? `${window.location.pathname}?${urlParams.toString()}` : window.location.pathname;
          window.history.replaceState({}, '', newUrl);
        }
      }

      // Clear from fired codes tracker so it can be re-applied if needed
      firedUrlParamCodesRef.current.delete(code);

      // Also remove from cart (cart will handle if course is not in cart)
      await couponStore.removeCouponCode(code);

      // Clear cached price and reload
      const courseIdNum = Number(course.id);
      coursePriceStore.priceMap.delete(courseIdNum);
      coursePriceStore.registerCourseById(courseIdNum);
    } catch (error) {
      console.error('[CLPPurchaseContext] Failed to remove coupon:', error);
    }
  }, [couponStore, coursePriceStore, course.id]);

  /**
   * Sync pricing API response to display state and URL
   *
   * This effect runs whenever the pricing API returns new data.
   * CLP ALWAYS uses pricing API data for display, regardless of cart state.
   * Cart state is separate and doesn't influence CLP display.
   *
   * URL management:
   * - Transform pricing API response → DiscountAttempt for display
   * - Sync URL based on pricing API validation:
   *   * Valid code → Keep couponCode in URL
   *   * Invalid/expired → Remove couponCode from URL
   * - Display uses pricingDiscountAttempts (from pricing API)
   * - Fire CouponApplyEvent for URL param codes when validated
   */
  useEffect(() => {
    if (coursePrice?.status !== 'loaded') {
      return;
    }

    // Convert MobX proxy to plain object for transform
    const plainCoursePrice = toJS(coursePrice);
    const discount = transformCoursePriceToDiscount(plainCoursePrice);

    // Manage pricing discount attempts and URL based on pricing API response
    const urlParams = new URLSearchParams(window.location.search);
    const urlCouponCode = urlParams.get('couponCode');

    // Case 1: Pricing API rejected the code in URL (invalid/expired)
    if (!coursePrice.code && urlCouponCode) {
      urlParams.delete('couponCode');
      const newUrl = urlParams.toString() ? `${window.location.pathname}?${urlParams.toString()}` : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
      return;
    }

    // Case 2: No discount - nothing else to do
    if (!discount || !coursePrice.code) {
      return;
    }

    // Check if we need to fire tracking event for URL param code
    const urlParamCodeMatches = urlCouponCode === coursePrice.code;
    const wasManuallyApplied = firedUrlParamCodesRef.current.has(coursePrice.code);

    // Do not fire event or update coupon store if the coupon has already been applied.
    if (wasManuallyApplied) {
      return;
    }

    // If the coupon code in the URL does not match the coupon code attached the course, then sync the URL param.
    if (!urlParamCodeMatches) {
      // Org members should never have couponCode written to the URL.
      if (!hasOrganization) {
        urlParams.set('couponCode', coursePrice.code);
        window.history.replaceState({}, '', `${window.location.pathname}?${urlParams.toString()}`);
      }

      // At this point, it's possible for a coupon to be applied in the pricing API, but for the CLP to have never considered it
      // to have been applied.
      // This can happen if the coupon is e.g, automatically applied as part of a campaign.
      // So we'll sync our tracking ref, and attempt to publish the coupon event.
      firedUrlParamCodesRef.current.add(coursePrice.code);
      // For legacy reasons, coupons applied automatically are considered 'url_param' coupons.
      publishCouponApplyEvent(coursePrice.code, 'url_param', buyables);

      // On initialization, the coupon store registers automatically applied coupons with itself.
      // So there's no need for us to do anything further in this case.
      return;
    }

    // Fire tracking event for URL param code validated by pricing API
    publishCouponApplyEvent(coursePrice.code, 'url_param', buyables);

    // Update coupon store state to reflect this code is applied
    couponStore.updateDiscountState({
      codesFromApi: [discount]
    });

    // Mark as tracked to prevent duplicate events
    firedUrlParamCodesRef.current.add(coursePrice.code);
  }, [coursePrice, coursePrice?.code, coursePrice?.discountPrice, coursePrice?.campaign, coursePrice?.status, couponStore, buyables, hasOrganization]);

  // Flag to determine if user has previously purchased the course - if so, should show "Go to course" CTA
  const isCourseEnrollmentStatusLoading = course.isEnrolled === undefined;
  const isEnrolledInCourse = !!course?.isEnrolled;

  // Enrollable simply indicates that the course is published and non-private - it says nothing about whether the user is actually eligible to enroll
  const isPublishedAndNonPrivate = course.enrollable;

  // Flag to determine if the course is free
  const isFree = course.isFree;

  // Flag to determine if the course is free from a discount
  // Only consider it free with discount if the price is loaded and actually $0
  const isFreeWithDiscount = coursePrice?.status === 'loaded' && coursePrice?.price?.amount === 0;

  // Check if pricing data is loaded - coursePrice.status === 'loaded' indicates pricing is ready
  const isPricingResultLoaded = coursePrice?.status === 'loaded';

  // Check content collection info to see if course is in Grow with Google subscription
  const isCourseIncludedInGrowWithGoogleSubscription = !!course.contentCollectionInfo?.isInGooglePartnerCollection;

  // Check if GwG plans are loading (only if course is in Google content collection).
  // If CLP already resolved any subscription availability for this course, don't block
  // on broader GwG plan context values that can stay in a loading sentinel state.
  const shouldWaitForGwgPlans = isCourseIncludedInGrowWithGoogleSubscription && !isCourseInAvailablePlans;
  // We treat GwG plan data as resolved once either GwG offer returns a non-loading value.
  const hasResolvedGwgPlan = gwgPlan?.isLoading === false;
  const hasResolvedGwgPlusPersonalPlan = gwgPlusPersonalPlan?.isLoading === false;
  const isGwGPlanLoading = Boolean(shouldWaitForGwgPlans && !hasResolvedGwgPlan && !hasResolvedGwgPlusPersonalPlan && (gwgPlan?.isLoading || gwgPlusPersonalPlan?.isLoading));
  const shouldEvaluateNonCatalogPPOffer = nonCatalogPPOfferEnabled && !isFeaturesLoading && !isCourseIncludedInUserSubscription && !course.isFree && !isCourseEnrollmentStatusLoading && !isEnrolledInCourse && !isPersonalPlanSubscriber && !!course.owner?.isOptedIntoDeals;

  // When subscription tiers is enabled, courses in the Value Plan catalog are in-catalog
  // and should not receive the non-catalog PP offer treatment.
  const isCourseInAnyCatalog = subscriptionTiersEnabled ? isCourseInPersonalPlanCatalog || isCourseInValuePlanCatalog : isCourseInPersonalPlanCatalog;
  const shouldFetchNonCatalogPPPricing = shouldEvaluateNonCatalogPPOffer && !isCourseInAnyCatalog && !isPersonalPlanLoading && !isPersonalPlanSubscriber;
  const nonCatalogPPPricing = useNonCatalogPPPricing({
    enabled: shouldFetchNonCatalogPPPricing
  });
  const isNonCatalogPPOfferLoading = shouldEvaluateNonCatalogPPOffer && (isPersonalPlanLoading || shouldFetchNonCatalogPPPricing && nonCatalogPPPricing.isLoading);

  // For UB users, we don't need to wait for pricing or consumer subs info
  // For MX users, if either pricing data or subs data is loading, show loading state
  // For free CLPs, short-circuit loading state since pricing isn't needed
  // Always show loading if enrollment status is still being determined
  // If user is already enrolled, don't show loading state
  // IMPORTANT: Once price has loaded once, don't show full loading state on price reloads
  // For GwG courses, also wait for GwG plan data to load
  const isLoading = isFeaturesLoading || isCtaBehaviourLoading || isCourseEnrollmentStatusLoading || isNonCatalogPPOfferLoading || !hasOrganization && !isEnrolledInCourse && !isFree && !isFreeWithDiscount && (!isPricingResultLoaded && !hasLoadedPriceOnce || isSubscriptionPlanLoading || isGwGPlanLoading);
  const isEligibleForNonCatalogPPOffer = shouldEvaluateNonCatalogPPOffer && !isCourseInAnyCatalog && !isPersonalPlanLoading && nonCatalogPPPricing.isAvailable;
  const isEligibleForBlendedOffer = blendedOfferEnabled && !isFeaturesLoading && isCourseInPersonalPlanCatalog && !isPersonalPlanSubscriber && !isCourseIncludedInUserSubscription && !!course.owner?.isOptedIntoDeals;

  // Shared gate for both the collapsible purchase section and the tabbed sidebar layout:
  // - Org members (Team Plan or UB) always use the org CTA, never these marketplace layouts
  // - Free courses, subscribers with course included, and enrolled users use the old purchase box
  const canUseAlternatePurchaseLayout = !!(!hasOrganization && !isFeaturesLoading && !isCourseIncludedInUserSubscription && !course.isFree && !isCourseEnrollmentStatusLoading && !isEnrolledInCourse);

  // Flags that, individually, mean we should use the collapsible layout instead of sidebar tabs.
  // NOTE: We use isEligibleForNonCatalogPPOffer (not shouldEvaluateNonCatalogPPOffer) to avoid
  // activating the collapsible layout when pricing data isn't available, which would show an
  // empty subscription section with just the "or" separator.
  const shouldUseCollapsibleLayout = collapsiblePurchaseBoxFeatureEnabled || isEligibleForNonCatalogPPOffer || subscriptionTiersEnabled || isEligibleForBlendedOffer || removeSubsOptionValue;

  // Business logic to determine if we should render the new collapsible purchase section:
  // - If the new purchase box experiment is on for all courses, OR
  // - If a non-catalog PP offer is confirmed eligible (pricing available) for this course
  const useCollapsiblePurchaseSection = !!(canUseAlternatePurchaseLayout && shouldUseCollapsibleLayout);

  // Business logic to determine if we should show UB-related content:
  // - Course is in an available subscription plan OR a non-catalog PP offer is eligible
  const showUdemyBusinessContent = !!(canUseAlternatePurchaseLayout && (isCourseInAvailablePlans || isEligibleForNonCatalogPPOffer));

  // Business logic to determine if we should render the tab container around the sidebar:
  // - If we need to show business content in other tabs, and:
  // - If the collapsible layout should be used instead (experiment, non-catalog PP offer,
  //   subscription tiers, blended offer, or explicit opt-out) - use that instead of tabs.
  // - If course is in Grow with Google subscription - don't show tabs
  const renderTabContainer = !!(showUdemyBusinessContent && !isCourseIncludedInGrowWithGoogleSubscription && !shouldUseCollapsibleLayout);

  // If this field is still undefined, we can't assume course is discounted
  const isTransactionalDiscountEnabled = coursePrice && !!coursePrice?.discountPrice;
  const isPersonalPlanDiscountEnabled = !!unwrappedConsumerSubscriptionPlan?.annualDiscount || !!unwrappedConsumerSubscriptionPlan?.monthlyDiscount;

  // By default, set the CTA behaviour to subs-first.
  // Also, use subs first as the default/control behaviour if we are unable to retrieve the cta behaviour.
  // Non-catalog PP eligible courses are treated the same as catalog courses for default selection.
  const hasSubscriptionOffer = !removeSubsOptionValue && (isCourseInAvailablePlans || isEligibleForNonCatalogPPOffer);
  const defaultPurchaseOption = useMemo(() => {
    if (isLoading) return CLPPurchaseOption.LOADING;

    // Non-catalog PP experiment: override purchase option based on flag variant
    if (nonCatalogCtaOrder && isEligibleForNonCatalogPPOffer) {
      return nonCatalogCtaOrder === 'subs_first' ? CLPPurchaseOption.SUBSCRIPTION : CLPPurchaseOption.TRANSACTIONAL;
    }

    // If the cta behaviour should be mx-first, set the selected cta to transactional.
    if (ctaBehaviour === CtaBehaviour.PURCHASE_FIRST) {
      return CLPPurchaseOption.TRANSACTIONAL;
    }

    // This is the centralized logic for the dynamic CTA behavior on CLP
    if (ctaBehaviour === CtaBehaviour.DYNAMIC) {
      return hasSubscriptionOffer && (!isTransactionalDiscountEnabled || isPersonalPlanDiscountEnabled) ? CLPPurchaseOption.SUBSCRIPTION : CLPPurchaseOption.TRANSACTIONAL;
    }
    return hasSubscriptionOffer ? CLPPurchaseOption.SUBSCRIPTION : CLPPurchaseOption.TRANSACTIONAL;
  }, [isLoading, ctaBehaviour, hasSubscriptionOffer, isTransactionalDiscountEnabled, isPersonalPlanDiscountEnabled, nonCatalogCtaOrder, isEligibleForNonCatalogPPOffer]);
  const [selectedPurchaseOption, setSelectedPurchaseOption] = useState(defaultPurchaseOption);
  useEffect(() => {
    setSelectedPurchaseOption(defaultPurchaseOption);
  }, [defaultPurchaseOption]);
  const {
    featureVariantAssignments
  } = useFeatures(['change_cta_language_for_pp']);
  const changeCtaLangFeatureAssignment = featureVariantAssignments.get('change_cta_language_for_pp')?.configuration as {
    getStarted: boolean;
    getPersonalPlan: boolean;
  } | undefined;

  // Business logic to determine whether or not we can show 'Lifetime access' copy for this course
  // This logic is used both in CourseIncludes / useCourseIncentives and the BuyBox
  const showLifetimeAccess = !isFree && !isPersonalPlanSubscriber && !hasOrganization;
  // We show 'Lifetime access' copy in the BuyBox only for PP courses where 'Course includes' is rendered in CLP body
  const showLifetimeAccessInBuyBox = showLifetimeAccess && isCourseInAvailablePlans && !isMobile;
  const isAnySubscriber = subscriptionTiersEnabled ? isPersonalPlanSubscriber || isValuePlanSubscriber : isPersonalPlanSubscriber;
  const showUpgradeCTA = !isCourseIncludedInUserSubscription && isCourseInAvailablePlans && isAnySubscriber;
  const blendedOfferCheckoutUrl = isEligibleForBlendedOffer ? `/subscription-checkout/express/?${new URLSearchParams({
    blended_offer: course.id
  }).toString()}` : undefined;
  const subscriptionCTAConfiguration: SubscriptionCTAConfiguration = useMemo(() => {
    return subscriptionTiersEnabled ? {
      directCheckout: (() => {
        // VP to PP upgrade -> direct checkout
        if (showUpgradeCTA && isValuePlanSubscriber) return true;
        // Non-VP subscribers (GwG) -> plan selection page
        if (showUpgradeCTA) return false;
        // MX users on Starter/VP courses -> plan comparison page
        // PP-only courses -> direct checkout
        if (!isAnySubscriber && isCourseInValuePlanCatalog) {
          return starterPlanUpsellCLPCopyUpdatesEnabled;
        }
        return true;
      })(),
      selectPlanText: showUpgradeCTA ? gettext('Upgrade your plan') : undefined,
      purchaseCtaText: (() => {
        if (isEligibleForBlendedOffer) return gettext('Get started');
        if (showUpgradeCTA) return gettext('Upgrade your plan');
        if (!isAnySubscriber && isCourseInValuePlanCatalog && starterPlanUpsellCLPCopyUpdatesEnabled) {
          return gettext('Start subscription');
        }
        return undefined;
      })(),
      upgradeUrl: (() => {
        if (blendedOfferCheckoutUrl) return blendedOfferCheckoutUrl;
        if (!showUpgradeCTA) return undefined;
        // VP subscribers upgrading to PP use a dedicated upgrade checkout URL
        if (isValuePlanSubscriber) {
          return '/subscription-checkout/upgrade/?upgrade_type=tier&product_type=CONSUMERSUBSCRIPTION';
        }
        // GwG subscribers -> plan selection page with GwG+PP product type
        if (subscribedProductType === SubscriptionPlanProductType.GrowWithGoogle) {
          return `/subscription-checkout/plans/?product_type=${SubscriptionPlanProductType.ConsumersubscriptionPlusGrowWithGoogle}`;
        }
        return undefined;
      })()
    } : {
      directCheckout: !showUpgradeCTA,
      selectPlanText: showUpgradeCTA ? gettext('Upgrade your plan') : undefined,
      purchaseCtaText: isEligibleForBlendedOffer ? gettext('Get started') : undefined,
      upgradeUrl: blendedOfferCheckoutUrl
    };
  }, [blendedOfferCheckoutUrl, gettext, isAnySubscriber, isCourseInValuePlanCatalog, isEligibleForBlendedOffer, isValuePlanSubscriber, showUpgradeCTA, starterPlanUpsellCLPCopyUpdatesEnabled, subscribedProductType, subscriptionTiersEnabled]);

  /** Each branch calls gettext with a static literal so locale extraction works. */
  const ctaText = (config: {
    getStarted: boolean;
    getPersonalPlan: boolean;
  } | undefined): string => {
    if (!config) {
      return gettext('Start subscription');
    }
    if (config.getStarted && !config.getPersonalPlan) {
      return gettext('Get started');
    }
    if (!config.getStarted && config.getPersonalPlan) {
      return gettext('Get Personal Plan');
    }
    return gettext('Start subscription');
  };

  // Non-catalog courses always go to direct subscription checkout (no trial)
  const nonCatalogSubscriptionCTAConfiguration = useMemo(() => isEligibleForNonCatalogPPOffer ? {
    directCheckout: true,
    purchaseCtaText: ctaText(changeCtaLangFeatureAssignment)
  } : undefined,
  // ctaText is a stable inline fn that only depends on gettext + the config arg
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [isEligibleForNonCatalogPPOffer, changeCtaLangFeatureAssignment, gettext]);

  // Discount attempts for display
  // IMPORTANT: Always use pricing API data for CLP, even when course is in cart
  // The pricing API is the source of truth for whether a discount is valid for THIS course
  const currentDiscountAttempts = pricingDiscountAttempts;

  // Additional calculations for the coupon error message.
  // Necessary to reconcile potential differences between the cart API and the pricing API.
  const couponErrorMessage = useMemo(() => {
    return _resolveCouponErrorMessage({
      currentMode: couponStore.currentMode,
      submittedCode: couponStore.submittedCode,
      couponStoreError: couponStore.error,
      currentDiscountAttempts,
      isReloadingPrice
    });
  }, [couponStore.currentMode, couponStore.submittedCode, couponStore.error, currentDiscountAttempts, isReloadingPrice]);

  // Memoize the main context value so it only produces a new reference when something
  // other than selectedPurchaseOption/setSelectedPurchaseOption changes.
  // Those two live exclusively in ReactCLPSelectedOptionContext, so a radio toggle
  // leaves this value referentially stable — React.memo on consumer subtrees can bail out.
  const purchaseContextValue = useMemo<CLPPurchaseContext>(() => ({
    isLoading,
    isReloadingPrice,
    isFree,
    isFreeWithDiscount,
    isPublishedAndNonPrivate: isPublishedAndNonPrivate,
    isEnrolledInCourse,
    isCourseInAvailablePlans,
    isCourseIncludedInUserSubscription,
    isCourseIncludedInGrowWithGoogleSubscription,
    isPersonalPlanSubscriber,
    isTransactionalDiscountEnabled,
    isPersonalPlanDiscountEnabled,
    unwrappedConsumerSubscriptionPlan,
    renderTabContainer,
    activeSidebarTab,
    setActiveSidebarTab,
    showLifetimeAccess,
    showLifetimeAccessInBuyBox,
    subscriptionCTAConfiguration,
    nonCatalogSubscriptionCTAConfiguration,
    couponStore,
    handleCouponApply,
    handleCouponRemove,
    currentDiscountAttempts,
    couponErrorMessage,
    showUdemyBusinessContent,
    useCollapsiblePurchaseSection,
    isEligibleForNonCatalogPPOffer,
    isFeaturesLoading,
    subscriptionTiersEnabled,
    clpGenericUdemyPlansCopyEnabled,
    starterPlanUpsellCLPCopyUpdatesEnabled,
    showUpgradeCTA,
    isEligibleForBlendedOffer,
    blendedOfferFeature,
    nonCatalogPersonalPlan: nonCatalogPPPricing.nonCatalogPersonalPlan,
    removeSubscriptionOptionEnabled: removeSubsOptionValue,
    gwgPlan,
    gwgPlusPersonalPlan,
    isMonthlyPersonalPlanAvailable,
    isConsumerSubscriptionPaused
  }), [isLoading, isReloadingPrice, isFree, isFreeWithDiscount, isPublishedAndNonPrivate, isEnrolledInCourse, isCourseInAvailablePlans, isCourseIncludedInUserSubscription, isCourseIncludedInGrowWithGoogleSubscription, isPersonalPlanSubscriber, isTransactionalDiscountEnabled, isPersonalPlanDiscountEnabled, unwrappedConsumerSubscriptionPlan, renderTabContainer, activeSidebarTab, showLifetimeAccess, showLifetimeAccessInBuyBox, subscriptionCTAConfiguration, nonCatalogSubscriptionCTAConfiguration, couponStore, handleCouponApply, handleCouponRemove, currentDiscountAttempts, couponErrorMessage, showUdemyBusinessContent, useCollapsiblePurchaseSection, isEligibleForNonCatalogPPOffer, isFeaturesLoading, subscriptionTiersEnabled, clpGenericUdemyPlansCopyEnabled, starterPlanUpsellCLPCopyUpdatesEnabled, showUpgradeCTA, isEligibleForBlendedOffer, blendedOfferFeature, nonCatalogPPPricing.nonCatalogPersonalPlan, removeSubsOptionValue, gwgPlan, gwgPlusPersonalPlan, isMonthlyPersonalPlanAvailable, isConsumerSubscriptionPaused]);
  return <ReactCLPSelectedOptionContext.Provider value={{
    selectedPurchaseOption,
    setSelectedPurchaseOption
  }}>
            <ReactCLPPurchaseContext.Provider value={purchaseContextValue}>
                {props.children}
            </ReactCLPPurchaseContext.Provider>
        </ReactCLPSelectedOptionContext.Provider>;
};

/**
 * Custom hook to access the CLP purchase context
 * @returns CLPPurchaseContext
 * @throws Error if used outside of CLPPurchaseContextProvider
 */
export function useClpPurchaseContext() {
  const context = useContext(ReactCLPPurchaseContext);
  if (context === undefined) {
    throw new Error("useClpPurchaseContext must be used within a CLPPurchaseContextProvider");
  }
  return context;
}